import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getYieldSimulatorProducts, fmt } from "@/lib/api";
import { Calculator, Droplets, Package, Plus, Trash2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";

let _rid = 0;
const newRow = () => ({ _rid: ++_rid, product_type: "", basis: "target", customYield: "", desiredQty: "" });

// Resolve the effective yield (kg product / 100 L milk) for a row given the chosen basis.
const effectiveYield = (row, meta) => {
  if (!meta) return null;
  if (row.basis === "custom") {
    const v = Number(row.customYield);
    return Number.isFinite(v) && v > 0 ? v : null;
  }
  if (row.basis === "average") return meta.avg_actual_yield_per_100l ?? null;
  return meta.target_yield_per_100l ?? null;
};

export default function YieldSimulator() {
  const { t, td } = useT();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("product-to-milk"); // or "milk-to-product"
  const [rows, setRows] = useState([newRow()]);
  const [availableMilk, setAvailableMilk] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getYieldSimulatorProducts();
        setProducts(data || []);
      } catch (e) {
        console.error("yield-sim: load failed", e);
        toast.error(t("yieldSim.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metaByName = useMemo(() => {
    const m = {};
    for (const p of products) m[p.product_type] = p;
    return m;
  }, [products]);

  const updateRow = (rid, patch) =>
    setRows((rs) => rs.map((r) => (r._rid === rid ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const removeRow = (rid) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r._rid !== rid) : rs));

  // When a product is picked, default the basis to the first available figure.
  const onPickProduct = (rid, name) => {
    const meta = metaByName[name];
    let basis = "custom";
    if (meta?.target_yield_per_100l != null) basis = "target";
    else if (meta?.avg_actual_yield_per_100l != null) basis = "average";
    updateRow(rid, { product_type: name, basis });
  };

  // Per-row computed result.
  const computed = rows.map((r) => {
    const meta = metaByName[r.product_type];
    const y = effectiveYield(r, meta); // kg / 100 L
    let milkNeeded = null;
    let output = null;
    if (y && y > 0) {
      if (mode === "product-to-milk") {
        const q = Number(r.desiredQty);
        if (Number.isFinite(q) && q > 0) milkNeeded = (q / y) * 100;
      } else {
        const milk = Number(availableMilk);
        if (Number.isFinite(milk) && milk > 0) output = (milk * y) / 100;
      }
    }
    return { row: r, meta, y, milkNeeded, output };
  });

  const totalMilk = computed.reduce((s, c) => s + (c.milkNeeded || 0), 0);
  const hasValidProducts = products.some((p) => p.target_yield_per_100l != null || p.avg_actual_yield_per_100l != null);

  const basisOptions = (meta) => [
    { value: "target", label: t("yieldSim.basisTarget"), sub: meta?.target_yield_per_100l != null ? `${fmt(meta.target_yield_per_100l, 2)}` : t("yieldSim.notSet"), disabled: meta?.target_yield_per_100l == null },
    { value: "average", label: t("yieldSim.basisAverage"), sub: meta?.avg_actual_yield_per_100l != null ? `${fmt(meta.avg_actual_yield_per_100l, 2)} (${meta.sample_count})` : t("yieldSim.noHistory"), disabled: meta?.avg_actual_yield_per_100l == null },
    { value: "custom", label: t("yieldSim.basisCustom"), sub: "", disabled: false },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="yield-simulator-page">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Calculator className="h-6 w-6 text-[hsl(var(--brand))]" />
            {t("yieldSim.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("yieldSim.subtitle")}</p>
        </div>

        {/* Mode toggle */}
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList data-testid="yield-sim-mode">
            <TabsTrigger value="product-to-milk" data-testid="yield-sim-mode-p2m">
              <Package className="mr-1.5 h-4 w-4" /> {t("yieldSim.modeProductToMilk")}
            </TabsTrigger>
            <TabsTrigger value="milk-to-product" data-testid="yield-sim-mode-m2p">
              <Droplets className="mr-1.5 h-4 w-4" /> {t("yieldSim.modeMilkToProduct")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {!loading && !hasValidProducts && (
          <Alert data-testid="yield-sim-no-data">
            <Info className="h-4 w-4" />
            <AlertTitle>{t("yieldSim.noDataTitle")}</AlertTitle>
            <AlertDescription>{t("yieldSim.noDataHint")}</AlertDescription>
          </Alert>
        )}

        {/* Milk-to-product: shared milk input */}
        {mode === "milk-to-product" && (
          <Card>
            <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:items-end sm:gap-4">
              <div className="max-w-xs flex-1 space-y-1.5">
                <Label htmlFor="available-milk">{t("yieldSim.availableMilk")}</Label>
                <div className="relative">
                  <Input id="available-milk" type="number" min="0" step="1" value={availableMilk}
                    onChange={(e) => setAvailableMilk(e.target.value)}
                    className="pr-10 text-right font-mono tabular-nums" data-testid="yield-sim-milk-input" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">L</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground sm:pb-2">{t("yieldSim.milkModeHint")}</p>
            </CardContent>
          </Card>
        )}

        {/* Rows table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{t("yieldSim.products")}</CardTitle>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="yield-sim-add-row">
              <Plus className="mr-1.5 h-4 w-4" /> {t("yieldSim.addProduct")}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">{t("yieldSim.product")}</TableHead>
                    <TableHead className="min-w-[150px]">{t("yieldSim.yieldBasis")}</TableHead>
                    <TableHead className="text-right">{t("yieldSim.yieldPer100")}</TableHead>
                    {mode === "product-to-milk" && <TableHead className="text-right min-w-[130px]">{t("yieldSim.desiredQty")}</TableHead>}
                    <TableHead className="text-right min-w-[140px]">{mode === "product-to-milk" ? t("yieldSim.milkNeeded") : t("yieldSim.productOutput")}</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {computed.map(({ row, meta, y, milkNeeded, output }) => (
                    <TableRow key={row._rid} data-testid={`yield-sim-row-${row._rid}`}>
                      {/* Product */}
                      <TableCell>
                        <Select value={row.product_type} onValueChange={(v) => onPickProduct(row._rid, v)}>
                          <SelectTrigger data-testid={`yield-sim-product-${row._rid}`}>
                            <SelectValue placeholder={t("yieldSim.selectProduct")} />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((p) => (
                              <SelectItem key={p.product_type} value={p.product_type}>{td(p.product_type)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {/* Basis */}
                      <TableCell>
                        <Select value={row.basis} onValueChange={(v) => updateRow(row._rid, { basis: v })} disabled={!row.product_type}>
                          <SelectTrigger data-testid={`yield-sim-basis-${row._rid}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {basisOptions(meta).map((o) => (
                              <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                                <span className="flex items-center gap-2">
                                  {o.label}
                                  {o.sub && <span className="text-xs text-muted-foreground">{o.sub}</span>}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {/* Effective yield */}
                      <TableCell className="text-right">
                        {row.basis === "custom" ? (
                          <div className="relative ml-auto max-w-[110px]">
                            <Input type="number" min="0" step="0.01" value={row.customYield}
                              onChange={(e) => updateRow(row._rid, { customYield: e.target.value })}
                              placeholder="0" className="pr-8 text-right font-mono tabular-nums"
                              data-testid={`yield-sim-custom-${row._rid}`} />
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">kg</span>
                          </div>
                        ) : (
                          <span className="font-mono tabular-nums" data-testid={`yield-sim-eff-${row._rid}`}>
                            {y != null ? `${fmt(y, 2)} kg` : "—"}
                          </span>
                        )}
                      </TableCell>
                      {/* Desired qty (product->milk) */}
                      {mode === "product-to-milk" && (
                        <TableCell className="text-right">
                          <div className="relative ml-auto max-w-[120px]">
                            <Input type="number" min="0" step="0.1" value={row.desiredQty}
                              onChange={(e) => updateRow(row._rid, { desiredQty: e.target.value })}
                              placeholder="0" className="pr-8 text-right font-mono tabular-nums"
                              data-testid={`yield-sim-qty-${row._rid}`} />
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">kg</span>
                          </div>
                        </TableCell>
                      )}
                      {/* Result */}
                      <TableCell className="text-right font-mono tabular-nums font-medium" data-testid={`yield-sim-result-${row._rid}`}>
                        {mode === "product-to-milk"
                          ? (milkNeeded != null ? <span className="text-[hsl(var(--brand))]">{fmt(milkNeeded, 1)} L</span> : "—")
                          : (output != null ? <span className="text-[hsl(var(--brand))]">{fmt(output, 2)} kg</span> : "—")}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]"
                          onClick={() => removeRow(row._rid)} disabled={rows.length <= 1}
                          data-testid={`yield-sim-remove-${row._rid}`} aria-label={t("common.delete")}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Total (product -> milk) */}
            {mode === "product-to-milk" && (
              <div className="mt-4 flex items-center justify-end gap-3 rounded-lg bg-muted/50 px-4 py-3" data-testid="yield-sim-total">
                <Droplets className="h-5 w-5 text-[hsl(var(--brand))]" />
                <span className="text-sm text-muted-foreground">{t("yieldSim.totalMilkNeeded")}</span>
                <span className="font-mono text-lg font-semibold tabular-nums text-[hsl(var(--brand))]">{fmt(totalMilk, 1)} L</span>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" /> {t("yieldSim.footnote")}
        </p>
      </div>
    </Layout>
  );
}
